// auditpm-v6
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase, AUDIT_TYPES } from '../lib/supabase'
import { calcBizSAFEMandays, resolveCT17Industry } from '../lib/ct17'
import { format, parseISO } from 'date-fns'
import { FirmHeader } from '../components/PublicComponents'
import { emailHtml, emailDate } from '../lib/emailHtml'
import { oneLine, isAddressField } from '../lib/oneLine'
import { toast, failed } from '../lib/toast'

// Same fields as the full application but with pre-fill and renewal framing
const RENEWAL_QUESTIONS = [
  // Pre-filled, read-only
  { id: 'company_name', label: 'Company full name', type: 'text', required: true, prefilled: true },
  { id: 'uen', label: 'Workplace No. / UEN No.', type: 'text', required: true, prefilled: true },
  // Client updates these
  { id: 'application_type', label: 'Application type', type: 'select', required: true, options: ['Renewal', 'Progression'], default: 'Renewal' },
  { id: 'current_level', label: 'Current bizSAFE level', type: 'select', required: true, options: ['bizSAFE Level 3', 'bizSAFE Level 4', 'bizSAFE STAR'] },
  { id: 'company_address', label: 'Company address', type: 'text', required: true, prefilled: true },
  { id: 'num_locations', label: 'Total number of fixed/permanent locations operating under your UEN (offices, branches, yards, permanent worksites). Do not count temporary project sites. If unsure, enter your best estimate.', type: 'number', required: true, min: 1, prefilled: true },
  { id: 'ssic_code', label: 'SSIC Code category', type: 'select', required: true, options: ['Construction F41-43', 'Manufacturing C10-32 (excl C301)', 'Marine C301/H52252/H52225', 'Transport & Storage H49-53 (excl H52252 & H52225)', 'None of the above'] },
  { id: 'audit_scope', label: 'Audit scope', type: 'select', required: true, options: ['Construction Worksite', 'Metalworking Industry', 'Shipyard', 'Oil Refinery or Petrochemical Plant', 'Semiconductor Wafer Fabrication Plant', 'Chemical Manufacturing Plant', 'Pharmaceutical Plant', 'Bulk Storage Terminal', 'Other Industries'] },
  { id: 'other_industry_desc', label: 'If "Other Industries", please describe', type: 'text', required: false },
  { id: 'business_nature', label: 'Company details (processes & operations)', type: 'text', required: true, prefilled: true },
  { id: 'num_employees', label: 'Size (no. of direct employees)', type: 'number', required: true, min: 1, prefilled: true },
  { id: 'outsourced_processes', label: 'Outsourced processes that affect conformity', type: 'select', required: true, options: ['Not applicable — all processes are not outsourced', 'Yes — some functions are outsourced'] },
  { id: 'outsourced_detail', label: 'If yes, list outsourced functions', type: 'text', required: false },
  { id: 'consultancy_provided', label: 'Has consultancy relating to RM been provided for this renewal?', type: 'select', required: true, options: ['No — consultancy has not been provided', 'Yes — consultancy has been provided'] },
  { id: 'consultancy_detail', label: 'If yes, consulting company and consultant name', type: 'text', required: false },
  { id: 'management_system_changes', label: 'Any significant changes to your management system since last audit?', type: 'select', required: true, options: ['No or not applicable', 'Yes'] },
  { id: 'management_system_changes_detail', label: 'If yes, elaborate', type: 'text', required: false },
  { id: 'key_personnel_changes', label: 'Any change of management structure and/or key personnel since last audit?', type: 'select', required: true, options: ['No or not applicable', 'Yes'] },
  { id: 'key_personnel_changes_detail', label: 'If yes, elaborate', type: 'text', required: false },
  { id: 'level1_holder', label: 'bizSAFE Level 1/TEWP certificate holder is still in the company', type: 'yesno', required: false },
  { id: 'level2_holder', label: 'bizSAFE Level 2 certificate holder is still in the company', type: 'yesno', required: false },
  { id: 'editable_rm_docs', label: 'We have the editable copies of our Risk Management documents', type: 'yesno', required: false },
  { id: 'pic_name', label: 'PIC (Person-in-charge) full name', type: 'text', required: true, prefilled: true },
  { id: 'pic_email', label: 'PIC email address', type: 'text', required: true, prefilled: true },
  { id: 'pic_phone', label: 'PIC contact number', type: 'text', required: true, prefilled: true },
  { id: 'preferred_audit_dates', label: 'Preferred audit dates (at least 1 month in advance)', type: 'text', required: false },
  { id: 'remarks', label: 'Any other remarks or questions', type: 'textarea', required: false },
]

function Field({ q, value, onChange, prefilled }) {
  // Address: always one line (line breaks become ", ").
  if (isAddressField(q)) { const set = onChange; q = { ...q, type: 'text' }; onChange = v => set(oneLine(v)) }
  const isPrefilled = q.prefilled && prefilled
  const baseStyle = {
    width: '100%', padding: '9px 11px',
    border: `0.5px solid ${isPrefilled ? 'rgba(26,95,168,0.3)' : 'rgba(0,0,0,0.22)'}`,
    borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none',
    background: isPrefilled ? '#f0f6ff' : '#fff', color: '#006363'
  }

  const label = (
    <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>
      {q.label}
      {q.required && <span style={{ color: '#c0392b', marginLeft: 3 }}>*</span>}
      {isPrefilled && <span style={{ marginLeft: 8, fontSize: 11, color: '#006363', fontWeight: 500 }}>pre-filled</span>}
    </label>
  )

  if (q.type === 'textarea') return (
    <div style={{ marginBottom: 16 }}>
      {label}
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...baseStyle, resize: 'vertical', minHeight: 72 }} />
    </div>
  )
  if (q.type === 'select') return (
    <div style={{ marginBottom: 16 }}>
      {label}
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={baseStyle}>
        <option value="">Select…</option>
        {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
  if (q.type === 'yesno') return (
    <div style={{ marginBottom: 16 }}>
      {label}
      <div style={{ display: 'flex', gap: 10 }}>
        {['Yes', 'No'].map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 18px', borderRadius: 8, border: `0.5px solid ${value === opt ? '#006363' : 'rgba(0,0,0,0.2)'}`, background: value === opt ? '#e0f2f2' : '#fff', cursor: 'pointer', fontSize: 14, fontWeight: value === opt ? 500 : 400, color: value === opt ? '#004a4a' : '#333' }}>
            <input type="radio" name={q.id} value={opt} checked={value === opt} onChange={() => onChange(opt)} style={{ display: 'none' }} />
            {opt}
          </label>
        ))}
      </div>
    </div>
  )
  return (
    <div style={{ marginBottom: 16 }}>
      {label}
      <input
        type={q.type === 'number' ? 'number' : q.type === 'date' ? 'date' : 'text'}
        min={q.type === 'number' ? q.min : undefined}
        onWheel={e => q.type === 'number' && e.currentTarget.blur()}
        value={value || ''} onChange={e => onChange(e.target.value)} style={baseStyle}
      />
    </div>
  )
}

export default function RenewalForm() {
  const { token } = useParams()
  const [link, setLink] = useState(null)
  const [client, setClient] = useState(null)
  const [responses, setResponses] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [alreadyUsed, setAlreadyUsed] = useState(false)

  useEffect(() => { loadForm() }, [token])

  async function loadForm() {
    // Token-scoped read via RPC (no broad anon table access)
    const { data: renewalLink, error } = await supabase.rpc('get_renewal_by_token', { p_token: token })
    if (error || !renewalLink || !renewalLink.client) { setInvalid(true); setLoading(false); return }
    if (renewalLink.used_at) { setAlreadyUsed(true); setLoading(false); return }

    setLink(renewalLink)
    const c = renewalLink.client
    setClient(c)

    const lastQuote = renewalLink.last_quote || null
    const prev = lastQuote?.form_responses || {}

    setResponses({
      company_name: c.name || '',
      uen: c.uen || '',
      company_address: oneLine(c.address || prev.company_address || ''),   // one line (older records may have line breaks)
      num_locations: prev.num_locations || '1',
      business_nature: prev.business_nature || c.industry || '',
      num_employees: prev.num_employees || '',
      pic_name: c.contact_name || '',
      pic_email: c.contact_email || '',
      pic_phone: c.contact_phone || '',
      // Carry forward audit scope from last quote
      audit_scope: prev.audit_scope || lastQuote?.audit_scope || '',
      ssic_code: prev.ssic_code || '',
      // Pre-fill ONLY from real quote history. NEVER default a level — an imported pre-launch client has no
      // quotes, and a hardcoded 'bizSAFE Level 3' made STAR clients submit as Level 3 (they trust pre-fills).
      // Blank + required forces an active choice instead.
      current_level: lastQuote?.bizsafe_level || '',
      application_type: 'Renewal',
      // These should always be re-confirmed
      outsourced_processes: '',
      consultancy_provided: '',
      management_system_changes: '',
      key_personnel_changes: '',
    })
    setLoading(false)
  }

  function set(id, val) { setResponses(r => ({ ...r, [id]: val })) }

  function validate() {
    return RENEWAL_QUESTIONS.every(q => {
      const v = responses[q.id]
      const filled = v != null && String(v).trim() !== ''
      if (q.required && !filled) return false
      if (q.type === 'number' && q.min != null && filled && Number(v) < q.min) return false  // e.g. 0 locations
      return true
    })
  }

  async function submit() {
    if (!validate()) { toast('Please complete the required fields', 'warning', { detail: 'Locations and employees must be at least 1' }); return }
    setSubmitting(true)
    const r = { ...responses, company_address: oneLine(responses.company_address).trim() }

    try {
      // Calculate man-days (client update + quote creation happen server-side)
      // Single source of truth — fixed scope resolves directly (was passing raw scope labels
      // straight through, which mis-calculated Construction/Oil/etc.).
      const industry = r.other_industry_desc
        ? 'Other - Low complexity'
        : resolveCT17Industry(r.audit_scope)
      const calcResult = calcBizSAFEMandays(industry, r.num_employees, r.num_locations || 1)

      const { error } = await supabase.rpc('submit_renewal_form', {
        p_token: token,
        p_payload: {
          audit_type: AUDIT_TYPES.BIZSAFE,
          // The renewal link already KNOWS the current cert expiry (the client is not asked again) — write it
          // into the standard questionnaire field so the quotation behaves exactly like a normal renewal quote
          // (expiry shows on the quote/questionnaire, drives the tracker and reminder lifecycle).
          form_responses: { ...r, expiry_date: link?.expiry_date || r.expiry_date || null },
          mandays: calcResult.totalMandays,
          calc_details: calcResult,
          initials: 'WT',
        },
      })
      if (error) throw error
      setSubmitted(true)
      // Best-effort internal notification (mirrors PublicForm) — goes to the office mailbox so the
      // whole team sees it (and it lands in Zoho Desk as a ticket via the M365 copy rule). NEVER
      // blocks the client's submission.
      try {
        const company = r.company_name || client?.name || '—'
        await supabase.functions.invoke('send-internal-email', {
          body: {
            to: 'bizSAFE@ccis.com.sg',
            subject: `Renewal Application Submitted – ${company}`,
            html: emailHtml({
              title: 'Renewal application submitted',
              greeting: 'Dear Team,',
              lines: ['A renewal application has just been submitted via the automated reminder link. Please review it and prepare the quotation.'],
              details: [['Company', company], ['UEN', r.uen || client?.uen || '—'], ['Current level', r.current_level || '—'],
                ['Current expiry', emailDate(link?.expiry_date)]],
              button: { label: 'Open Quotations →', url: `${window.location.origin}/quotations` },
            }),
          },
        })
      } catch (e) { console.error('renewal notification failed (non-blocking):', e) }
    } catch (e) {
      console.error(e)
      failed("We couldn't submit your form", e.message, 'Please try again')
    }
    setSubmitting(false)
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3' }}>
      <div style={{ color: '#999', fontSize: 14 }}>Loading…</div>
    </div>
  )

  if (invalid) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 420, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Invalid link</h2>
        <p style={{ color: '#666', fontSize: 14 }}>This renewal link is not valid. Please contact us at query@ccis.com.sg.</p>
      </div>
    </div>
  )

  if (alreadyUsed) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 440, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Already submitted</h2>
        <p style={{ color: '#666', fontSize: 14 }}>This renewal application has already been submitted. We will prepare your quotation shortly.</p>
        <p style={{ color: '#888', fontSize: 13, marginTop: 12 }}>Questions? Contact us at query@ccis.com.sg</p>
      </div>
    </div>
  )

  if (submitted) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 36, maxWidth: 480, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 36, marginBottom: 14 }}>✓</div>
        <h2 style={{ fontWeight: 500, marginBottom: 10, fontSize: 18 }}>Renewal application received</h2>
        <p style={{ color: '#555', fontSize: 14, lineHeight: 1.7 }}>
          Thank you, <strong>{responses.pic_name || client?.contact_name}</strong>. We have received your renewal application for <strong>{client?.name}</strong>.
        </p>
        <p style={{ color: '#555', fontSize: 14, lineHeight: 1.7, marginTop: 10 }}>
          We will prepare your quotation within 1–2 business days and send it to {responses.pic_email || client?.contact_email}.
        </p>

      </div>
    </div>
  )

  const expiryFormatted = link?.expiry_date ? format(parseISO(link.expiry_date), 'd MMMM yyyy') : ''

  return (
    <div style={{ minHeight: '100vh', background: '#f7f6f3', padding: '40px 20px 60px' }}>
      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        <FirmHeader />
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontWeight: 500, fontSize: 20, marginBottom: 8 }}>bizSAFE Renewal Application</h1>
          {expiryFormatted && (
            <div style={{ display: 'inline-block', background: '#faeeda', color: '#633806', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 500, marginBottom: 12 }}>
              Current certification expires: {expiryFormatted}
            </div>
          )}
          <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>
            Your company details have been pre-filled. Please check they are still correct and update anything that has changed. Fields marked <span style={{ color: '#c0392b' }}>*</span> are required.
          </p>
        </div>

        <div style={{ background: '#e0f2f2', border: '0.5px solid rgba(26,95,168,0.25)', borderRadius: 8, padding: '11px 16px', marginBottom: 20, fontSize: 13, color: '#004a4a' }}>
          Fields highlighted in blue are pre-filled from your previous application. Please verify they are still accurate.
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', border: '0.5px solid rgba(0,0,0,0.1)' }}>
          {RENEWAL_QUESTIONS.map(q => (
            <Field
              key={q.id}
              q={q}
              value={responses[q.id]}
              onChange={v => set(q.id, v)}
              prefilled={!!responses[q.id] && q.prefilled}
            />
          ))}
          <button
            onClick={submit}
            disabled={submitting}
            style={{ marginTop: 8, width: '100%', padding: '12px 20px', borderRadius: 10, border: 'none', background: '#006363', color: '#fff', fontWeight: 500, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {submitting ? 'Submitting…' : 'Submit renewal application'}
          </button>
        </div>

      </div>
    </div>
  )
}